import { computed, Injectable, signal } from '@angular/core';

import { AuthState, AuthenticatedUser, Permission } from '../models';
import { SELECTED_ACCOUNTS_SESSION_KEY } from '../../constants/session-storage.constants';

const INITIAL_AUTH_STATE: AuthState = {
  status: 'idle',
  isAuthenticated: false,
  user: null,
  permissions: []
};

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private readonly state = signal<AuthState>(INITIAL_AUTH_STATE);

  readonly authState = this.state.asReadonly();
  readonly user = computed(() => this.state().user);
  readonly permissions = computed(() => this.state().permissions);
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly status = computed(() => this.state().status);
  readonly roleNames = computed(() => this.state().user?.roles ?? []);
  readonly permissionSlugs = computed(() => this.state().permissions.map((permission) => permission.slug));

  beginBootstrap(): void {
    this.patchState({ status: 'loading' });
  }

  completeBootstrap(user: AuthenticatedUser, permissions: Permission[]): void {
    this.state.set({
      status: 'ready',
      isAuthenticated: true,
      user,
      permissions
    });
  }

  failBootstrap(): void {
    this.patchState({ status: 'error' });
  }

  clearSession(): void {
    this.state.set(INITIAL_AUTH_STATE);
    try {
      sessionStorage.removeItem(SELECTED_ACCOUNTS_SESSION_KEY);
    } catch {
      // Session storage can be unavailable in restricted browser contexts.
    }
  }

  updatePreferredLanguage(preferredLanguage: string): void {
    this.state.update((currentState) => {
      if (!currentState.user) {
        return currentState;
      }

      return {
        ...currentState,
        user: {
          ...currentState.user,
          preferredLanguage
        }
      };
    });
  }

  private patchState(partial: Partial<AuthState>): void {
    this.state.update((currentState) => ({
      ...currentState,
      ...partial
    }));
  }
}